/* assets/adv-tabs.js */

/* --------------------------------------------------------
   CLASS: TABBED SECTION
---------------------------------------------------------*/
class TabbedSection {
  constructor(container) {
    this.container = container;
    this.tabButtons = Array.from(container.querySelectorAll('.tab-button'));
    this.tabContents = Array.from(container.querySelectorAll('.tab-content'));
    this.init();
  }

  init() {
    // click listeners
    this.tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchTab(button);
      });

      // keyboard navigation (left/right)
      button.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          this.focusNext(button);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.focusPrev(button);
        }
      });
    });

    // Activate first tab on load (or the one marked active)
    this.activateFirstTab();
  }

  switchTab(button) {
    const targetId = button.getAttribute('data-tab');

    // Deactivate all
    this.tabButtons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });
    this.tabContents.forEach(content => {
      content.classList.remove('active');
      content.setAttribute('aria-hidden', 'true');
    });

    // Activate clicked button
    button.classList.add('active');
    button.setAttribute('aria-selected', 'true');

    // Activate matching content
    const targetContent = this.container.querySelector(`[data-content="${targetId}"]`);
    if (targetContent) {
      targetContent.classList.add('active');
      targetContent.setAttribute('aria-hidden', 'false');
    }
  }

  activateFirstTab() {
    if (this.tabButtons.length === 0) return;

    // If any button already has .active, keep it; otherwise activate first
    const activeBtn = this.tabButtons.find(b => b.classList.contains('active')) || this.tabButtons[0];
    this.switchTab(activeBtn);
  }

  focusNext(current) {
    const idx = this.tabButtons.indexOf(current);
    const next = this.tabButtons[(idx + 1) % this.tabButtons.length];
    next.focus();
    // optionally switch on focus:
    // this.switchTab(next);
  }

  focusPrev(current) {
    const idx = this.tabButtons.indexOf(current);
    const prevIndex = (idx - 1 + this.tabButtons.length) % this.tabButtons.length;
    const prev = this.tabButtons[prevIndex];
    prev.focus();
    // optionally switch on focus:
    // this.switchTab(prev);
  }
}

/* --------------------------------------------------------
   CLASS: ADVANCED ACCORDION
---------------------------------------------------------*/
class AdvancedAccordion {
  constructor(container) {
    this.container = container;
    this.buttons = Array.from(container.querySelectorAll('.acc-btn'));
    this.init();
  }

  init() {
    this.buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        const content = btn.nextElementSibling;
        const isOpen = btn.classList.contains('active');

        // Close all items in this accordion
        this.buttons.forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-expanded', 'false');
          const c = b.nextElementSibling;
          if (c) {
            c.style.maxHeight = null;
          }
        });

        // Toggle clicked item
        if (!isOpen) {
          btn.classList.add('active');
          btn.setAttribute('aria-expanded', 'true');
          if (content) {
            content.style.maxHeight = content.scrollHeight + "px";
          }
        } else {
          // already closed by reset above
        }
      });

      // ensure collapsed state initially
      btn.setAttribute('aria-expanded', 'false');
      const c = btn.nextElementSibling;
      if (c) c.style.maxHeight = null;
    });
  }
}

/* --------------------------------------------------------
   INITIALIZERS
---------------------------------------------------------*/
function initTabbedSections() {
  document.querySelectorAll('.tabbed-section').forEach(section => {
    // Only initialize once per section
    if (!section.__tabbed_inited) {
      section.__tabbed_inited = true;
      new TabbedSection(section);
    } else {
      // re-bind in case DOM changed
      new TabbedSection(section);
    }
  });
}

function initAccordions() {
  document.querySelectorAll('.adv-accordion').forEach(acc => {
    if (!acc.__accordion_inited) {
      acc.__accordion_inited = true;
      new AdvancedAccordion(acc);
    }
  });
}

function initializeAll() {
  initTabbedSections();
  initAccordions();
}

/* --------------------------------------------------------
   DOM READY INIT
---------------------------------------------------------*/
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAll);
} else {
  initializeAll();
}

/* --------------------------------------------------------
   SHOPIFY EDITOR SUPPORT
---------------------------------------------------------*/
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeAll();
  });

  document.addEventListener('shopify:section:reorder', () => {
    initializeAll();
  });

  document.addEventListener('shopify:block:select', (event) => {
    // When editor selects a block, open the corresponding tab
    const blockEl = event.target;
    const section = blockEl.closest('.tabbed-section');
    if (!section) return;

    // The Shopify editor sets data-block-id attribute on the block element in the editor canvas.
    const selectedBlockId = blockEl.getAttribute('data-block-id') || blockEl.getAttribute('data-block') || null;

    if (selectedBlockId) {
      const btn = section.querySelector(`.tab-button[data-tab="${selectedBlockId}"]`);
      if (btn) {
        // switch to that tab
        const instance = new TabbedSection(section);
        instance.switchTab(btn);
      }
    }
  });
}
